import React, { useState } from 'react';
import { ArrowRightIcon, HandIcon, PlusIcon } from 'lucide-react';
import { InviteDialog } from '../../components/invite/InviteDialog';
import { InitialAvatar } from '../../components/InitialAvatar';
import { Button } from '../../components/ui/Button';
import { PANEL_PHOTOS } from '../../data/registration';
import { CURRENT_MEMBER, DECADES, arrivalsThisWeek, communityTotals, decadeCounts, membersByDecade, scaleMembers } from '../../data/community';
import { BadgeMark } from '../../components/profile/MemberBadges';
import { CollectiveTile } from '../../components/dashboard/CollectiveTile';
import { WhatsNewTile } from '../../components/dashboard/WhatsNewTile';
interface HomeMosaicProps {
  sparse: boolean;
  onOpenPeople: () => void;
}
const DECADE_PHOTOS: Record<string, {
  src: string;
  alt: string;
}> = {
  '1970s': PANEL_PHOTOS.founding,
  '1980s': PANEL_PHOTOS.building,
  '1990s': PANEL_PHOTOS.room,
  '2000s': PANEL_PHOTOS.hands,
  '2010s': PANEL_PHOTOS.era,
  '2020s': PANEL_PHOTOS.voices
};
export function HomeMosaic({
  sparse,
  onOpenPeople
}: HomeMosaicProps) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const members = scaleMembers(sparse);
  const counts = decadeCounts(sparse);
  const totals = communityTotals(sparse);
  const yourPeople = membersByDecade(members, CURRENT_MEMBER.decade).filter(member => member.name !== CURRENT_MEMBER.fullName);
  const arrivals = arrivalsThisWeek(sparse);
  return <div className="flex flex-col gap-3 p-3 sm:p-4" data-id="element-603" data-mp="iosnmdwqw0m5">
      {/* TIME AXIS — the decade ribbon doubles as the page header. */}
      <section aria-label="Fifty years" className="overflow-hidden rounded-card bg-ink" data-id="element-604" data-mp="9qglbeminedv">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-4 pb-3 pt-4 sm:px-5" data-id="element-605" data-mp="eo8oj4sm2mxd">
          <h1 className="text-heading font-semibold text-on-ink" data-id="element-606" data-mp="2ufd17cui5eyz">
            {CURRENT_MEMBER.firstName} — you sit in {CURRENT_MEMBER.years}
          </h1>
          <p className="text-small text-on-ink opacity-70" data-id="element-607" data-mp="14z0xsvzuhjk8">
            {totals.members} members · {totals.collectiveYears} collective years represented
          </p>
        </div>

        <ol className="flex snap-x gap-px overflow-x-auto bg-ink pb-4 pl-4 pr-4 sm:pl-5" data-id="element-608" data-mp="1020rcjz32esl">
          {DECADES.map(decade => {
          const isYours = decade === CURRENT_MEMBER.decade;
          const photo = DECADE_PHOTOS[decade];
          return <li key={decade} className="relative w-36 shrink-0 snap-start overflow-hidden rounded-field sm:w-44" data-id="element-609" data-mp="m9c9hlqi11du">
                <img src={photo.src} alt={photo.alt} className="h-24 w-full object-cover sm:h-28" data-id="element-610" data-mp="ca3gav3gjqsj" />
                <div aria-hidden="true" className={`absolute inset-0 bg-ink ${isYours ? 'opacity-25' : 'opacity-60'}`} data-id="element-611" data-mp="8aum5p4i9qwg" />
                <div className="absolute inset-x-0 bottom-0 flex flex-col gap-0.5 p-2.5" data-id="element-612" data-mp="22nassr17r71u">
                  <span className="text-small font-semibold text-on-ink" data-id="element-613" data-mp="3kjmmr998lbfx">{decade}</span>
                  <span className="text-eyebrow-caption font-medium uppercase text-on-ink opacity-80" data-id="element-614" data-mp="3pih2217zwhva">
                    {counts[decade]} {counts[decade] === 1 ? 'member' : 'members'}
                  </span>
                </div>
                {isYours ? <span className="absolute left-2.5 top-2.5 inline-flex items-center rounded-pill bg-highlight px-2 py-0.5 text-eyebrow-caption font-semibold uppercase text-on-highlight" data-id="element-615" data-mp="31ljsog1u0ob9">
                    You
                  </span> : null}
              </li>;
        })}
        </ol>
      </section>

      {/* MOSAIC — people, archive, acknowledgement and contribution at once. */}
      <div className="grid gap-3 lg:grid-cols-12" data-id="element-616" data-mp="pwiqataviutc">
        <section aria-labelledby="archive-tile" className="relative overflow-hidden rounded-card lg:col-span-7" data-id="element-617" data-mp="pvr7yk6aetl0">
          <img src={PANEL_PHOTOS.era.src} alt="Tuesday night group, winter 1996" className="h-72 w-full object-cover sm:h-80 lg:h-full lg:min-h-[19rem]" data-id="element-618" data-mp="tdoqy5o3i30t" />
          <div aria-hidden="true" className="absolute inset-0 bg-ink opacity-55" data-id="element-619" data-mp="12f4m566c9ykg" />
          <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 p-5" data-id="element-620" data-mp="yd1b2inl0vzp">
            <span className="flex items-center gap-2 text-eyebrow-caption font-semibold uppercase text-on-ink" data-id="element-621" data-mp="yf4aftamk1h3">
              <span aria-hidden="true" className="h-px w-5 bg-highlight" data-id="element-622" data-mp="17dpobb55isbj" />
              Added to the archive · 1996
            </span>
            <h2 id="archive-tile" className="text-section-heading font-semibold text-on-ink" data-id="element-623" data-mp="1m2vlyuoccahb">
              Nobody has named the people in this one yet.
            </h2>
            <p className="max-w-md text-small text-on-ink opacity-80" data-id="element-624" data-mp="1v3ab654fe9q3">
              Winter, the back room. You were here that year — you may be one of the few who can
              say who&apos;s in it.
            </p>
            <div className="flex flex-wrap gap-2 pt-1" data-id="element-625" data-mp="29pha4y4zfmir">
              <Button data-id="element-626" data-mp="29nwfz9mky5lf">Help name them</Button>
              <Button variant="secondary" data-id="element-627" data-mp="2epc3d8c8co6z">Skip</Button>
            </div>
          </div>
        </section>

        <section aria-labelledby="people-tile" className="flex flex-col rounded-card border border-rule bg-card lg:col-span-5" data-id="element-628" data-mp="20q2qahjbma78">
          <div className="flex items-baseline justify-between gap-3 border-b border-rule px-5 py-4" data-id="element-629" data-mp="24squud104uye">
            <h2 id="people-tile" className="text-heading font-semibold text-ink" data-id="element-630" data-mp="1vq7wa54914hy">
              Here when you were
            </h2>
            <span className="text-small text-ink-muted" data-id="element-631" data-mp="1h23d701aaqyn">{CURRENT_MEMBER.years}</span>
          </div>

          {yourPeople.length > 0 ? <ul className="flex flex-col divide-y divide-rule" data-id="element-632" data-mp="1m0obuv0w12kz">
              {yourPeople.map(member => <li key={member.name} className="flex items-center gap-3 px-5 py-3" data-id="element-633" data-mp="182qaqrbkv2nf">
                  <InitialAvatar name={member.name} size={40} data-id="element-634" data-mp="1cyx6xse68zs6" />
                  <div className="flex min-w-0 flex-1 flex-col" data-id="element-635" data-mp="3ep5xb30yeqe5">
                    <span className="flex items-center gap-1.5 truncate text-body font-medium text-ink" data-id="element-636" data-mp="39y5yfs89xy90">
                      {member.name.split(' ')[0]} {member.name.split(' ')[1]?.charAt(0)}.
                      <BadgeMark name={member.name} data-id="element-637" data-mp="30xhk0weyxvvl" />
                    </span>
                    <span className="truncate text-small text-ink-muted" data-id="element-638" data-mp="30vek9ko0kodo">
                      {member.years}
                      {member.note ? ` · ${member.note}` : ''}
                    </span>
                  </div>
                  <button type="button" className="inline-flex h-11 w-11 items-center justify-center rounded-pill border border-rule text-ink-muted hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-639" data-mp="3on25pn2ib78k">
                    <span className="sr-only" data-id="element-640" data-mp="2880vi9ugyz4n">Wave at {member.name.split(' ')[0]}</span>
                    <HandIcon aria-hidden="true" className="h-4 w-4" data-id="element-641" data-mp="nrueatnbby0l" />
                  </button>
                </li>)}
            </ul> : <p className="px-5 py-6 text-small text-ink-muted" data-id="element-642" data-mp="wrhdd8skkrqh">
              Nobody from your years yet. We&apos;ll tell you the day someone arrives.
            </p>}

          <button type="button" onClick={onOpenPeople} className="mt-auto flex min-h-tap items-center gap-2 border-t border-rule px-5 text-left text-small font-medium text-primary-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-643" data-mp="11kahbug7rw1i">
            Everyone from the {CURRENT_MEMBER.decade}
            <ArrowRightIcon aria-hidden="true" className="h-4 w-4" data-id="element-644" data-mp="1kephvoi6bwrg" />
          </button>
        </section>

        <section aria-labelledby="wave-tile" className="flex flex-col justify-between gap-4 rounded-card bg-primary p-5 lg:col-span-4" data-id="element-645" data-mp="3m8f54rttah13">
          <div className="flex flex-col gap-2" data-id="element-646" data-mp="33abwmh7f8csb">
            <span className="text-eyebrow-caption font-semibold uppercase text-on-primary opacity-80" data-id="element-647" data-mp="2ydlhpx7crn8h">
              While you were away
            </span>
            <h2 id="wave-tile" className="text-section-heading font-semibold text-on-primary" data-id="element-648" data-mp="3c3p0ge70wyt6">
              Andre P. waved at you.
            </h2>
            <p className="text-small text-on-primary opacity-90" data-id="element-649" data-mp="2kcsn7ch1ws5d">
              No message, nothing to answer. He saw your years and wanted you to know he&apos;s
              here.
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-650" data-mp="2tf1t1p1djxcf">
            <Button variant="secondary" data-id="element-651" data-mp="2tbmdkl3sgmah">
              <HandIcon aria-hidden="true" className="h-4 w-4" data-id="element-652" data-mp="2oemg2yl08ruz" />
              Wave back
            </Button>
          </div>
        </section>

        <section aria-labelledby="arrivals-tile" className="flex flex-col rounded-card border border-rule bg-card lg:col-span-4" data-id="element-653" data-mp="8lx28i2animj">
          <div className="flex flex-col gap-0.5 border-b border-rule px-5 py-4" data-id="element-654" data-mp="c4mc512r81ma">
            <h2 id="arrivals-tile" className="text-heading font-semibold text-ink" data-id="element-655" data-mp="g7sfo0yhfep2">
              New arrivals this week
            </h2>
            <p className="text-small text-ink-muted" data-id="element-656" data-mp="292hehzxf8z4">
              {arrivals.count} {arrivals.count === 1 ? 'person' : 'people'} joined
              {arrivals.topDecade ? ` — ${arrivals.topDecade.count} from the ${arrivals.topDecade.decade}` : ''}
            </p>
          </div>
          <ul className="flex flex-col divide-y divide-rule" data-id="element-657" data-mp="zuglh8nv781s">
            {arrivals.people.map(person => <li key={person.name} className="flex items-center gap-3 px-5 py-2.5" data-id="element-658" data-mp="l49cdjjqz1dr">
                <InitialAvatar name={person.name} size={32} data-id="element-659" data-mp="q3m34fdqoslm" />
                <span className="flex flex-1 items-center gap-1.5 truncate text-small text-ink" data-id="element-660" data-mp="1duriktfrpive">
                  {person.name}
                  <BadgeMark name={person.name} data-id="element-661" data-mp="1in2fdnf7oojq" />
                </span>
                <span className="text-small text-ink-muted" data-id="element-662" data-mp="1ni668irecgnv">{person.decade}</span>
              </li>)}
          </ul>
          <p className="mt-auto border-t border-rule px-5 py-3 text-small text-ink-muted" data-id="element-663" data-mp="1sfepyns70sr8">
            {sparse ? 'Small, but it was twelve of us last month too.' : 'Everyone who joined, whatever decade they came from.'}
          </p>
        </section>

        <section aria-labelledby="invite-tile" className="flex flex-col justify-between gap-4 rounded-card border border-rule bg-card p-5 lg:col-span-4" data-id="element-664" data-mp="14ptuj5eus312">
          <div className="flex flex-col gap-2" data-id="element-665" data-mp="vqqde6z8xxp7">
            <h2 id="invite-tile" className="text-heading font-semibold text-ink" data-id="element-666" data-mp="vnuf2hjhl708">
              One of the two people you invited has joined.
            </h2>
            <p className="text-small text-ink-muted" data-id="element-667" data-mp="lssqbmt4i6y6">
              Ken D. is in. The other invitation is still open and doesn&apos;t expire.
            </p>
          </div>
          <Button variant="secondary" onClick={() => setInviteOpen(true)} data-id="element-668" data-mp="74itxop33yqr">
            <PlusIcon aria-hidden="true" className="h-4 w-4" data-id="element-669" data-mp="76vjoo133ded" />
            Invite someone from your years
          </Button>
        </section>

        <section aria-labelledby="thin-years" className="flex flex-col gap-4 rounded-card border border-rule bg-card p-5 sm:flex-row sm:items-center sm:justify-between lg:col-span-12" data-id="element-670" data-mp="h49pltpdsjsf">
          <div className="flex max-w-xl flex-col gap-1.5" data-id="element-671" data-mp="q2lmuekr6ftu">
            <span className="flex items-center gap-2 text-eyebrow-caption font-semibold uppercase text-ink-muted" data-id="element-672" data-mp="l53h0x218gq2">
              <span aria-hidden="true" className="h-px w-5 bg-highlight" data-id="element-673" data-mp="ztzkegt595kc" />
              Thin years
            </span>
            <h2 id="thin-years" className="text-section-heading font-semibold text-ink" data-id="element-674" data-mp="uvxzdqc5qw68">
              1994 has two entries in it.
            </h2>
            <p className="text-small text-ink-muted" data-id="element-675" data-mp="14st08fvwl8no">
              You were here that year. Anything you write about it isn&apos;t one account among
              many — it&apos;s one of two.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-3" data-id="element-676" data-mp="1gukfcu33vqfq">
            <ul aria-hidden="true" className="hidden gap-2 sm:flex" data-id="element-677" data-mp="1gsr9hh0bha8s">
              {[PANEL_PHOTOS.room, PANEL_PHOTOS.hands].map(photo => <li key={photo.src} className="h-16 w-16 overflow-hidden rounded-field" data-id="element-678" data-mp="1ql5ced34smwj">
                  <img src={photo.src} alt="" className="h-full w-full object-cover" data-id="element-679" data-mp="1un0h7rk9tif2" />
                </li>)}
              <li className="flex h-16 w-16 items-center justify-center rounded-field border border-dashed border-rule text-small text-ink-muted" data-id="element-680" data-mp="1zkabbkilui98">
                +
              </li>
            </ul>
            <Button data-id="element-681" data-mp="29ige04c8jsf5">Add something from 1994</Button>
          </div>
        </section>

        <div className="lg:col-span-8" data-id="element-682" data-mp="a7f56xgptiio">
          <CollectiveTile sparse={sparse} data-id="element-683" data-mp="a8qch79dd8ej" />
        </div>

        <div className="lg:col-span-4" data-id="element-684" data-mp="f6rudov271of">
          <WhatsNewTile data-id="element-685" data-mp="f7ukvvogz23t" />
        </div>
      </div>

      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} data-id="element-686" data-mp="2sg9r2butaxau" />
    </div>;
}